import type { HovSkillDefinition, HovSkillId } from "./hovSkillDefinitions";

export const HOV_SKILL_TREE_CELL_SIZE = 104;
export const HOV_SKILL_TREE_ICON_SIZE = 72;

export interface HovSkillNodeLayout {
  readonly left: number;
  readonly top: number;
  readonly centerX: number;
  readonly centerY: number;
}

export interface HovSkillLineLayout {
  readonly from: HovSkillId;
  readonly to: HovSkillId;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface HovSkillTreeLayout {
  readonly width: number;
  readonly height: number;
  readonly nodes: ReadonlyMap<HovSkillId, HovSkillNodeLayout>;
  readonly lines: readonly HovSkillLineLayout[];
}

export function computeHovSkillTreeLayout(
  definitions: readonly HovSkillDefinition[],
): HovSkillTreeLayout {
  const rows = groupDefinitionsByLogicalRow(definitions);
  let maxRow = 0;
  let maxRowSkillCount = 1;

  for (const [row, rowDefinitions] of rows) {
    if (row > maxRow) maxRow = row;
    if (rowDefinitions.length > maxRowSkillCount) {
      maxRowSkillCount = rowDefinitions.length;
    }
  }

  const width = maxRowSkillCount * HOV_SKILL_TREE_CELL_SIZE;
  const height = (maxRow + 1) * HOV_SKILL_TREE_CELL_SIZE;
  const nodes = new Map<HovSkillId, HovSkillNodeLayout>();

  for (const [logicalRow, rowDefinitions] of rows) {
    const sortedRowDefinitions = [...rowDefinitions].sort(
      (a, b) => a.column - b.column,
    );
    const rowWidth = sortedRowDefinitions.length * HOV_SKILL_TREE_CELL_SIZE;
    const rowStartX = (width - rowWidth) / 2;

    sortedRowDefinitions.forEach((definition, visualColumnIndex) => {
      const centerX =
        rowStartX +
        visualColumnIndex * HOV_SKILL_TREE_CELL_SIZE +
        HOV_SKILL_TREE_CELL_SIZE / 2;
      const centerY =
        logicalRow * HOV_SKILL_TREE_CELL_SIZE + HOV_SKILL_TREE_CELL_SIZE / 2;
      nodes.set(definition.id, {
        left: centerX - HOV_SKILL_TREE_ICON_SIZE / 2,
        top: centerY - HOV_SKILL_TREE_ICON_SIZE / 2,
        centerX,
        centerY,
      });
    });
  }

  return {
    width,
    height,
    nodes,
    lines: computePrerequisiteLines(definitions, nodes),
  };
}

function groupDefinitionsByLogicalRow(
  definitions: readonly HovSkillDefinition[],
): Map<number, HovSkillDefinition[]> {
  const rows = new Map<number, HovSkillDefinition[]>();
  for (const definition of definitions) {
    const rowDefinitions = rows.get(definition.row);
    if (rowDefinitions) {
      rowDefinitions.push(definition);
    } else {
      rows.set(definition.row, [definition]);
    }
  }
  return new Map([...rows].sort(([a], [b]) => a - b));
}

function computePrerequisiteLines(
  definitions: readonly HovSkillDefinition[],
  nodes: ReadonlyMap<HovSkillId, HovSkillNodeLayout>,
): HovSkillLineLayout[] {
  const lines: HovSkillLineLayout[] = [];
  for (const definition of definitions) {
    const to = nodes.get(definition.id);
    if (!to) continue;

    for (const prerequisiteId of definition.prerequisites) {
      const from = nodes.get(prerequisiteId);
      if (!from) continue;

      lines.push({
        from: prerequisiteId,
        to: definition.id,
        x1: from.centerX,
        y1: from.centerY,
        x2: to.centerX,
        y2: to.centerY,
      });
    }
  }
  return lines;
}
